/**
 * Plan Check Performance — how long each agency takes to review our submittals, measured from our
 * own Permit Activity log (Permitting Hub → Plan Check Performance; docs/PLAN_CHECK_PERFORMANCE.md).
 *
 * Built to be opened in a meeting with a jurisdiction's building department, so every number is a
 * plain measurement off logged events with its definition shown on screen — never an estimate.
 *
 * HOW A REVIEW IS READ (one agency review = one Agency Approvals row, its activity in date order):
 *   - a submittal (submitted / resubmitted, permit revisions included) hands the ball to the agency
 *     and, if no round is open, starts a ROUND (the first one is the "first review", later ones are
 *     "rechecks");
 *   - "Agency Has Requested Items" hands the ball back to us but leaves the round open — the days we
 *     spend getting them what they asked for are ours, not theirs, so they are taken out of the round;
 *   - comments / corrections close the round and hand the ball to us;
 *   - an approval closes the round and ends the review;
 *   - "Status Update" entries while the agency holds the ball are counted as our follow-ups.
 * A round still open at the end is "in review now", measured to today — unless nothing has been
 * logged on the review for STALE_DAYS, which means its outcome was never logged (kept apart).
 *
 * Calendar days throughout; business days (weekdays, holidays NOT removed) are shown beside them
 * for the 30-business-day line in Health & Safety Code 17960.3 (AB 253, 2025).
 */
import { apiGetAll } from './_api.js'

const TBL_REVIEWS  = 'tbl2Yu7YtDZ6tFFhI'   // Agency Approvals
const TBL_ACTIVITY = 'tblLO4mxHR04VCk5v'   // Permit Activity

const REVIEW_FIELDS = [
  'Name', 'Status', 'Agency Text', 'Agency Type Text', 'Type of Permit (from Permits)',
  'Project Name and Sub Name (from Permits)', 'Project Number (from Project) (from Permits)',
  'Project Record UID (from Project) (from Permits)', 'Agency Tracking Number',
]
const ACTIVITY_FIELDS = ['Date', 'Activity Type', 'Agency Approval', 'Created']

const SUBMIT   = /Submitted to Agency|Resubmitted to Agency|Submitted Permit Revision|Resubmitted Permit Revision/
const REQUEST  = /Agency Has Requested Items/
const COMMENTS = /Received Agency Comments|Received Agency Corrections/
const APPROVAL = /Received Agency Approval|Agency Approved Permit Revision|Received Committee Approval/
const CHECK    = /Status Update/
const REVISION = /Permit Revision/
// An open round with nothing logged on its review for this long is a review whose outcome was never
// logged, not one still in review. It is listed apart and kept out of every total.
const STALE_DAYS = 120

const first = v => Array.isArray(v) ? v[0] : v
const fieldsQs = list => list.map(f => 'fields%5B%5D=' + encodeURIComponent(f)).join('&')

/* ── Dates ─────────────────────────────────────────────────────────────── */
const toDate = s => new Date(s + 'T12:00:00')
export const todayIso = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') }
export const calDays = (a, b) => Math.max(0, Math.round((toDate(b) - toDate(a)) / 864e5))
/** Weekdays after `a` up to and including `b` (holidays not removed). */
export function busDays(a, b) {
  let n = 0
  const d = toDate(a), end = toDate(b)
  while (d < end) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) n++ }
  return n
}

/** The date `n` business days after `a` (weekdays; holidays not removed). */
export function addBusDays(a, n) {
  const d = toDate(a)
  let k = 0
  while (k < n) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) k++ }
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

/* ── Load ──────────────────────────────────────────────────────────────── */
let _cache = null   // one load per page session; the dashboard's Refresh clears it

/** Every agency review with its rounds measured. */
export async function loadPlanCheckData({ refresh = false } = {}) {
  if (_cache && !refresh) return _cache
  const [reviews, acts] = await Promise.all([
    apiGetAll(TBL_REVIEWS, fieldsQs(REVIEW_FIELDS) + '&pageSize=100', null, { strict: true }),
    apiGetAll(TBL_ACTIVITY, fieldsQs(ACTIVITY_FIELDS) + '&pageSize=100', null, { strict: true }),
  ])
  const byReview = new Map()
  for (const a of acts) {
    const f = a.fields || {}
    if (!f['Date'] || !f['Activity Type']) continue
    for (const id of (f['Agency Approval'] || [])) {
      if (!byReview.has(id)) byReview.set(id, [])
      byReview.get(id).push({ date: f['Date'], type: f['Activity Type'], created: f['Created'] || '' })
    }
  }
  const today = todayIso()
  _cache = reviews.map(r => measureReview(r, byReview.get(r.id) || [], today)).filter(r => r.rounds.length)
  return _cache
}

function measureReview(r, acts, today) {
  const f = r.fields || {}
  acts.sort((x, y) => x.date.localeCompare(y.date) || x.created.localeCompare(y.created))
  const out = {
    id: r.id,
    agency: first(f['Agency Text']) || '',
    agencyType: first(f['Agency Type Text']) || '',
    projectId: first(f['Project Record UID (from Project) (from Permits)']) || null,
    projectName: String(first(f['Project Name and Sub Name (from Permits)']) || f['Name'] || '').trim(),
    projectNumber: first(f['Project Number (from Project) (from Permits)']) || '',
    permitType: first(f['Type of Permit (from Permits)']) || '',
    status: first(f['Status']) || '',
    firstSubmitted: null, approvedOn: null,
    rounds: [], agencyDays: 0, ourDays: 0, ourTurnarounds: [],
    segments: [],   // { who: 'agency'|'us', start, days } — period totals sum these
  }
  let ball = null, since = null, round = null
  const seg = (who, from, n) => { if (who === 'agency') out.agencyDays += n; else out.ourDays += n; out.segments.push({ who, start: from, days: n }) }
  const closeAgency = date => { if (ball === 'agency') { const n = calDays(since, date); seg('agency', since, n); if (round) round.agencyDays += n } }
  const closeRound = (date, outcome) => {
    if (!round) return
    round.end = date; round.outcome = outcome
    if (round._segStart) round.businessDays += busDays(round._segStart, date)
    delete round._segStart
    out.rounds.push(round); round = null
  }
  for (const a of acts) {
    const t = a.type, d = a.date
    if (SUBMIT.test(t)) {
      if (!out.firstSubmitted) out.firstSubmitted = d
      if (ball === 'us') { const n = calDays(since, d); seg('us', since, n); if (!round) out.ourTurnarounds.push({ start: since, days: n }) }
      if (ball !== 'agency') { ball = 'agency'; since = d }
      if (!round) {
        const kind = REVISION.test(t) ? 'revision' : (out.rounds.some(x => x.kind !== 'revision') ? 'recheck' : 'first')
        round = { kind, start: d, end: null, outcome: null, agencyDays: 0, businessDays: 0, checks: 0, _segStart: d }
      } else if (!round._segStart) round._segStart = d
    } else if (REQUEST.test(t)) {
      if (ball === 'agency') {
        closeAgency(d)
        if (round && round._segStart) { round.businessDays += busDays(round._segStart, d); round._segStart = null }
        ball = 'us'; since = d
      }
    } else if (COMMENTS.test(t)) {
      if (ball === 'agency' && round) { closeAgency(d); closeRound(d, 'comments'); ball = 'us'; since = d }
      else if (ball === 'us' && !round) {
        // More comments before we resubmit = another department finishing the same round (Laguna
        // Beach releases comments department by department). The round runs to the last of them.
        const last = out.rounds[out.rounds.length - 1]
        if (last && last.outcome === 'comments' && last.end === since) {
          const n = calDays(since, d)
          last.agencyDays += n; last.businessDays += busDays(since, d); last.end = d
          seg('agency', since, n); since = d
        }
      }
    } else if (APPROVAL.test(t)) {
      if (ball === 'agency') closeAgency(d)
      if (round) closeRound(d, 'approval')
      if (!out.approvedOn) out.approvedOn = d
      ball = null; since = null
    } else if (CHECK.test(t)) {
      if (ball === 'agency' && round) round.checks++
    }
  }
  if (round) {
    const lastDate = acts.length ? acts[acts.length - 1].date : round.start
    if (calDays(lastDate, today) > STALE_DAYS) {
      round.stale = true; round.lastActivity = lastDate
    } else {
      // Still with the agency (or waiting on items we owe) — measured to today.
      if (ball === 'agency') { const n = calDays(since, today); round.agencyDays += n; seg('agency', since, n) }
      if (round._segStart) round.businessDays += busDays(round._segStart, today)
    }
    delete round._segStart
    round.open = true
    out.rounds.push(round)
  }
  return out
}

/* ── Summaries ─────────────────────────────────────────────────────────── */
export function median(a) {
  if (!a.length) return null
  const b = [...a].sort((x, y) => x - y), m = Math.floor(b.length / 2)
  return b.length % 2 ? b[m] : Math.round((b[m - 1] + b[m]) / 2)
}

/** The period filter: a round belongs to the period it STARTED in. */
export function inPeriod(dateIso, period) {
  if (!dateIso) return false
  if (period === 'all') return true
  if (period === '12m') { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return toDate(dateIso) >= d }
  return dateIso.slice(0, 4) === period
}

/**
 * Everything the dashboard shows for one agency (or for every agency when `agency` is null),
 * over rounds that started in `period`.
 */
export function summarize(reviews, agency, period) {
  const rs = reviews.filter(r => !agency || r.agency === agency)
  const all = rs.flatMap(r => r.rounds.map(x => ({ ...x, review: r }))).filter(x => inPeriod(x.start, period))
  const stale = all.filter(x => x.stale)
  const rounds = all.filter(x => !x.stale)
  // Same-day rounds (approved or handed comments at the counter the day submitted) and permit
  // revisions are not plan check; they are counted beside the medians, never inside them.
  const isOtc = x => !x.open && x.agencyDays === 0
  const done = rounds.filter(x => !x.open && !isOtc(x))
  const firsts = done.filter(x => x.kind === 'first')
  const rechecks = done.filter(x => x.kind === 'recheck')
  const touched = new Set(rounds.map(x => x.review.id))
  const rsIn = rs.filter(r => touched.has(r.id))
  const segs = rsIn.flatMap(r => r.segments).filter(g => inPeriod(g.start, period))
  const agencyDays = segs.filter(g => g.who === 'agency').reduce((s, g) => s + g.days, 0)
  const ourDays = segs.filter(g => g.who === 'us').reduce((s, g) => s + g.days, 0)
  return {
    reviews: rsIn,
    rounds,
    firstMedian: median(firsts.map(x => x.agencyDays)),
    firstBusMedian: median(firsts.map(x => x.businessDays)),
    firstCount: firsts.length,
    firstOver30Bus: firsts.filter(x => x.businessDays > 30).length,
    recheckMedian: median(rechecks.map(x => x.agencyDays)),
    recheckCount: rechecks.length,
    agencyShare: agencyDays + ourDays ? agencyDays / (agencyDays + ourDays) : null,
    agencyDays, ourDays,
    ourTurnaroundMedian: median(rsIn.flatMap(r => r.ourTurnarounds).filter(t => inPeriod(t.start, period)).map(t => t.days)),
    otcCount: rounds.filter(isOtc).length,
    stale,
    revisionCount: rounds.filter(x => x.kind === 'revision' && !isOtc(x)).length,
    checksPerRound: median(done.filter(x => x.kind !== 'revision').map(x => x.checks)),
    totalChecks: rounds.reduce((s, x) => s + x.checks, 0),
    openNow: rounds.filter(x => x.open),
    approvals: rsIn.filter(r => r.approvedOn && inPeriod(r.approvedOn, period)).length,
  }
}

/** Agencies we have measured rounds with, most reviews first. */
export function agencyList(reviews) {
  const m = new Map()
  for (const r of reviews) {
    if (!r.agency) continue
    const e = m.get(r.agency) || { agency: r.agency, type: r.agencyType, reviews: 0 }
    e.reviews++; m.set(r.agency, e)
  }
  return [...m.values()].sort((a, b) => b.reviews - a.reviews || a.agency.localeCompare(b.agency))
}
